import { TFunction } from 'i18next';

import { Maybe, MessageCheckin } from '@graphql/generated';
import theme from '@themes/theme';

type Attachment = {
  __typename?: string;
  [key: string]: any;
};

/**
 * Height of one line of the banner label, used to box the icon.
 *
 * The banner centres a fixed 15x15 icon against its label. Centring it against
 * the whole text block put it between the lines once the label wrapped on a
 * narrow screen, so the icon is boxed to a single line height and the row is
 * top-aligned — which keeps it beside the first line either way.
 */
export const CHECKIN_BANNER_LINE_HEIGHT = theme.textVariants.metadata1
  .lineHeight as number;

export type CheckinBannerProps = {
  color: string;
  iconName: string;
  label: string;
};

/**
 * Whether a server-computed geofence verdict is available for this check-in.
 *
 * TA-10635: `withinGeofence` is computed server-side, but a check-in that was
 * built locally (CheckinModal / checkInMapUtils) carries a hardcoded `false`
 * until the message is published. Rendering that as a verdict flashed a red
 * "location doesn't match" for the whole send round trip before it flipped to
 * the real result.
 *
 * - `ready`       — the check-in came from the server, so `withinGeofence` is real.
 * - `pending`     — locally built, send still in flight; no verdict exists yet.
 * - `unavailable` — locally built and the send will not complete, so no verdict
 *                   is ever coming.
 */
export type CheckinVerdictStatus = 'ready' | 'pending' | 'unavailable';

/**
 * Derive whether a server-computed verdict is available for `checkin`.
 *
 * TA-10635: keyed on the check-in's provenance, not the message's draft state.
 * A locally-built check-in is created with an empty id (CheckinModal,
 * checkInMapUtils) whereas anything server-sourced has a real one — including
 * a forwarded copy, which is still an unpublished draft but already carries
 * the original message's verdict and must not be re-verified.
 */
export const getCheckinVerdictStatus = (
  checkin: Maybe<MessageCheckin> | undefined,
  isSendFailed: boolean
): CheckinVerdictStatus => {
  if (checkin?.id) return 'ready';
  return isSendFailed ? 'unavailable' : 'pending';
};

type GetCheckinBannerPropsArgs = {
  attachments: Maybe<Attachment[]> | undefined;
  checkin: Maybe<MessageCheckin> | undefined;
  authorName: string;
  t: TFunction;
  verdictStatus?: CheckinVerdictStatus;
};

export const getCheckinBannerProps = ({
  attachments,
  checkin,
  authorName,
  t,
  verdictStatus = 'ready',
}: GetCheckinBannerPropsArgs): CheckinBannerProps => {
  const hasProject = (attachments || []).some(
    (att) => att.__typename === 'MessageProject'
  );

  // Only a project-tagged check-in renders a match/mismatch verdict, so it is
  // the only one that has to wait for the server. Without a project both
  // outcomes read the same, so verdict status cannot change what is shown.
  if (!hasProject) {
    return {
      color: 'blue',
      iconName: 'MapPin',
      label: t('models:chat.checkin.userCheckedIn', { authorName }),
    };
  }

  if (verdictStatus === 'pending') {
    return {
      color: 'grey04',
      iconName: 'MapPin',
      label: t('models:chat.checkin.verifyingLocation'),
    };
  }

  // No verdict is coming. Report the check-in without claiming anything about
  // the geofence — asserting a mismatch would be the false verdict this ticket
  // is about, and holding "verifying" would never resolve.
  if (verdictStatus === 'unavailable') {
    return {
      color: 'blue',
      iconName: 'MapPin',
      label: t('models:chat.checkin.userCheckedIn', { authorName }),
    };
  }

  if (checkin?.withinGeofence) {
    return {
      color: 'blue',
      iconName: 'MapPin',
      label: t('models:chat.checkin.userCheckedInForThisProject', {
        authorName,
      }),
    };
  }

  return {
    color: 'alertRed',
    iconName: 'Info',
    label: t('models:chat.checkin.locationDoesNotMatchWithProjectAddress'),
  };
};
